import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { UserRound, Pencil } from "lucide-react";
import { apiClient } from "../lib/apiClient";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Breadcrumbs } from "../components/layout/Breadcrumbs";
import { PageTitle } from "../components/layout/PageTitle";
import { RoleGate } from "../components/RoleGate";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ErrorState } from "../components/ErrorState";
import { EmptyState } from "../components/EmptyState";
import { Modal } from "../components/Modal";
import { getApiErrorMessage } from "../lib/apiClient";

interface StudentEntry {
  id: string;
  name: string;
  icNumber: string;
  age: number;
}

interface ParentWithStudents {
  id: string;
  name: string;
  phone: string;
  students: StudentEntry[];
}

async function fetchParents(search: string): Promise<ParentWithStudents[]> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  const { data } = await apiClient.get<{ parents: ParentWithStudents[] }>(`/parents?${params.toString()}`);
  return data.parents;
}

async function updateParent(id: string, input: { name?: string; phone?: string }): Promise<void> {
  await apiClient.patch(`/parents/${id}`, input);
}

async function updateStudent(id: string, input: { name?: string; icNumber?: string; age?: number }): Promise<void> {
  await apiClient.patch(`/parents/students/${id}`, input);
}

function EditParentModal({
  parent,
  onClose,
}: {
  parent: ParentWithStudents | null;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (parent) {
      setName(parent.name);
      setPhone(parent.phone);
    }
  }, [parent]);

  const mutation = useMutation({
    mutationFn: () => updateParent(parent!.id, { name, phone }),
    onSuccess: () => {
      toast.success("Parent updated");
      queryClient.invalidateQueries({ queryKey: ["parents"] });
      onClose();
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal
      open={!!parent}
      onClose={onClose}
      title="Edit Parent"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="edit-parent-form" className="btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving..." : "Save"}
          </button>
        </>
      }
    >
      <form id="edit-parent-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="global-parent-name">
            Name
          </label>
          <input id="global-parent-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="label" htmlFor="global-parent-phone">
            Phone
          </label>
          <input id="global-parent-phone" className="input" value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </div>
      </form>
    </Modal>
  );
}

function EditStudentModal({
  student,
  onClose,
}: {
  student: StudentEntry | null;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [icNumber, setIcNumber] = useState("");
  const [age, setAge] = useState("");

  useEffect(() => {
    if (student) {
      setName(student.name);
      setIcNumber(student.icNumber);
      setAge(String(student.age));
    }
  }, [student]);

  const mutation = useMutation({
    mutationFn: () => updateStudent(student!.id, { name, icNumber, age: Number(age) }),
    onSuccess: () => {
      toast.success("Student updated");
      queryClient.invalidateQueries({ queryKey: ["parents"] });
      onClose();
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal
      open={!!student}
      onClose={onClose}
      title="Edit Student"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="edit-student-form" className="btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving..." : "Save"}
          </button>
        </>
      }
    >
      <form id="edit-student-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="global-student-name">
            Name
          </label>
          <input id="global-student-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="label" htmlFor="global-student-ic">
            IC number
          </label>
          <input id="global-student-ic" className="input" value={icNumber} onChange={(e) => setIcNumber(e.target.value)} required />
        </div>
        <div>
          <label className="label" htmlFor="global-student-age">
            Age
          </label>
          <input
            id="global-student-age"
            type="number"
            min={0}
            max={120}
            className="input"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            required
          />
        </div>
      </form>
    </Modal>
  );
}

function ParentsPageContent(): JSX.Element {
  const [search, setSearch] = useState("");
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["parents", search],
    queryFn: () => fetchParents(search),
  });
  const [editingParent, setEditingParent] = useState<ParentWithStudents | null>(null);
  const [editingStudent, setEditingStudent] = useState<StudentEntry | null>(null);

  return (
    <DashboardLayout>
      <Breadcrumbs items={[{ label: "Parents & Students" }]} />
      <PageTitle icon={UserRound} title="Parents & Students" description="Global directory across all events." />

      <div className="mt-4">
        <input
          className="input max-w-sm"
          placeholder="Search by name or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="mt-6">
        {isLoading && <LoadingSpinner />}
        {isError && <ErrorState message={getApiErrorMessage(error)} />}
        {data && data.length === 0 && <EmptyState title="No parents found" />}
        {data && data.length > 0 && (
          <div className="card divide-y divide-slate-100 dark:divide-slate-700">
            {data.map((parent) => (
              <div
                key={parent.id}
                className="cursor-pointer p-4 transition-colors duration-100 hover:bg-slate-100 dark:hover:bg-slate-700/60"
                onClick={() => setEditingParent(parent)}
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{parent.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{parent.phone}</p>
                  </div>
                  <button
                    type="button"
                    className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors duration-150"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingParent(parent);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                </div>
                <ul className="mt-2 space-y-1">
                  {parent.students.map((s) => (
                    <li key={s.id} className="flex items-center justify-between gap-2 text-sm text-slate-600 dark:text-slate-300">
                      <span>
                        {s.name} - IC {s.icNumber} - Age {s.age}
                      </span>
                      <button
                        type="button"
                        className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors duration-150"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingStudent(s);
                        }}
                      >
                        <Pencil className="h-3 w-3" />
                        Edit
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      <EditParentModal parent={editingParent} onClose={() => setEditingParent(null)} />
      <EditStudentModal student={editingStudent} onClose={() => setEditingStudent(null)} />
    </DashboardLayout>
  );
}

export default function ParentsPage(): JSX.Element {
  return (
    <RoleGate roles={["SUPER_ADMIN"]}>
      <ParentsPageContent />
    </RoleGate>
  );
}
